import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, NgModule, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AppConfigService } from '@data-terminal/data-access/app-config/app-config.service';
import { APP_RUNTIME, AppRuntime, DOCU_URL, IS_PRODUCTION } from '@data-terminal/data-access/injection-tokens';
import { NavigationService } from '@data-terminal/data-access/navigation/navigation.service';
import { ErrorNotification } from '@data-terminal/data-access/notifications/notification.model';
import { OrganizationService } from '@data-terminal/data-access/organization';
import { BatchInformationDialogService } from '@data-terminal/feature-dialogs/services/batch-information/batch-information-dialog.service';
import { StackInformationDialogService } from '@data-terminal/feature-dialogs/services/job-flow/stack-information-dialog.service';
import { ApiRequestType } from '@data-terminal/shared-models/api-request-type.enum';
import { ErrorCode } from '@data-terminal/shared-models/error-correction';
import { SnackbarClass } from '@data-terminal/shared-models/global';
import { ROUTE_PATH } from '@data-terminal/shared-models/navigation';
import { TaskFormWebComponentLauncherService } from '@data-terminal/ui-presentational/dialogs/task-form-dialog/task-form-web-component-launcher.service';
import {
    ClientApiErrorDialogComponent,
    ClientApiErrorDialogData,
    ClientApiErrorDialogResponse,
} from '@data-terminal/ui-smart/dialogs';
import { NetworkStatusService } from '@data-terminal/ui-smart/services/network-status.service';
import { FeatureRequiredOnPremVersion } from '@data-terminal/utils/constants';
import { isFeatureEnabledForVersion } from '@data-terminal/utils/functions';
import { SnackBarService } from '@data-terminal/utils/services/snack-bar.service';
import { NotificationService } from '@heidelberg/control-center-frontend-integration';
import { CCAuthService } from '@heidelberg/control-center-frontend-integration/auth';
import {
    HDMUI_MAIN_APPLICATION_HEADER_CLICK_EVENT,
    HdmuiMainApplicationHeaderClickEventData,
    HdmuiMainApplicationHeaderComponent,
    HdmuiMainApplicationHeaderHelpItem,
    HdmuiMainApplicationHeaderSidenavLinks,
    HdmuiMainApplicationHeaderUser,
    HdmuiNotification,
    HdmuiNotificationPriority,
} from '@heidelberg/hdmui-angular';
import { ChatbotWidgetIconButtonComponent } from '@heidelberg/hplus-ngx-chatbot-integration';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, switchMap, take, tap } from 'rxjs';

const INDEX_0 = 0;
const INDEX_1 = 1;
const INDEX_2 = 2;
const INDEX_3 = 3;
const STATUS_ACTION_REQUEST_TYPES: readonly ApiRequestType[] = [
    ApiRequestType.OPERATIONSTART,
    ApiRequestType.OPERATIONPAUSE,
    ApiRequestType.OPERATIONFINISH,
];

@UntilDestroy()
@Component({
    selector: 'app-toolbar',
    templateUrl: './toolbar.component.html',
    standalone: false,
})
export class ToolbarComponent implements OnInit {
    readonly #isProduction = inject(IS_PRODUCTION);
    readonly #navigationService = inject(NavigationService);
    readonly #ccAuthService = inject(CCAuthService);
    readonly #notificationService = inject(NotificationService);
    readonly #dialog = inject(MatDialog);
    readonly #router = inject(Router);
    readonly #translateService = inject(TranslateService);
    readonly #batchInformationDialogService = inject(BatchInformationDialogService);
    readonly #stackInformationDialogService = inject(StackInformationDialogService);
    readonly #organizationService = inject(OrganizationService);
    readonly #taskFormWebComponentLauncherService = inject(TaskFormWebComponentLauncherService);
    readonly #appConfigService = inject(AppConfigService);
    readonly #appRuntime = inject(APP_RUNTIME);
    readonly #snackBarService = inject(SnackBarService);
    readonly #documentationUrl = inject(DOCU_URL)
        .replace('{{LANGUAGE}}', this.#translateService.getCurrentLang())
        .toLowerCase();
    readonly #networkStatusService = inject(NetworkStatusService);

    @Input() public hdmuiMainApplicationHeaderUser!: HdmuiMainApplicationHeaderUser;

    @Output() public logoutAction = new EventEmitter<void>();

    public controlCenterStage: 'stage' | undefined;
    public appStage: string | undefined;
    public orgId: string;

    public sidenavLinks: HdmuiMainApplicationHeaderSidenavLinks = {};
    public helpLinks: HdmuiMainApplicationHeaderHelpItem[] = [];
    public isTaskPilotEnabled = false;

    constructor() {
        this.controlCenterStage = this.#isProduction ? undefined : 'stage';
        this.orgId = this.#ccAuthService.getCurrentMembership()?.organizationId || '';
        this.appStage = this.#isProduction ? undefined : this.#appConfigService.getStage();
        this.sidenavLinks = {
            settings: { routerLink: [this.orgId, 'settings'] },
        };
        this.helpLinks = [
            {
                locoKey: 'DC.DASHBOARD_TOOLBAR.DOCS',
                action: { callback: () => this.openDocumentation() },
            },
        ];
    }

    public ngOnInit(): void {
        this.#organizationService.organizationInfo$.pipe(untilDestroyed(this)).subscribe((info) => {
            this.isTaskPilotEnabled = !!info.taskpilot;
        });
        if (this.orgId) {
            this.#notificationService.setCurrentUser({
                personId: this.hdmuiMainApplicationHeaderUser.id,
                organizationId: this.orgId,
            });
            this.#networkStatusService.isOnline$
                .pipe(
                    untilDestroyed(this),
                    tap((isOnline) => this.#notificationService.setIsPollingActive(isOnline)),
                    switchMap((isOnline) => (isOnline ? this.#notificationService.notifications$ : EMPTY))
                )
                .subscribe((notifications) => {
                    this.checkForErrorNotifications(notifications as unknown as HdmuiNotification[]);
                    this.checkForNoMoreStacksNotification(notifications as unknown as HdmuiNotification[]);
                });
        }
    }

    public isOnlinePressWebComponent(): boolean {
        return this.#appRuntime === AppRuntime.ONLINE_PRESS_WEB_COMPONENT;
    }

    public doLogout(): void {
        this.logoutAction.emit();
    }

    public goBack(): void {
        this.#navigationService.goToParentRoute();
    }

    public onMainApplicationHeaderClick(event: HdmuiMainApplicationHeaderClickEventData): void {
        switch (event.event) {
            case HDMUI_MAIN_APPLICATION_HEADER_CLICK_EVENT.LOGOUT:
                this.logoutAction.emit();
                break;
            case HDMUI_MAIN_APPLICATION_HEADER_CLICK_EVENT.BACK_BUTTON:
                this.#navigationService.goToParentRoute();
                break;
            case HDMUI_MAIN_APPLICATION_HEADER_CLICK_EVENT.CREATE_TASK:
                this.#taskFormWebComponentLauncherService.open();
                break;
        }
    }

    public onNotificationsSettingsClick(): void {
        const orgId = this.#ccAuthService.getCurrentMembership()?.organizationId;
        void this.#router.navigateByUrl(`${orgId}/${ROUTE_PATH.notifications}`);
    }

    public onBatchInformationClick(): void {
        this.#batchInformationDialogService.openRequestBatchInformationDialog();
    }

    public onStackInformationClick(): void {
        this.#stackInformationDialogService.openRequestStackInformationDialog();
    }

    public onDieCuttingToolsClick(): void {
        const orgId = this.#ccAuthService.getCurrentMembership()?.organizationId;
        void this.#router.navigateByUrl(`/${orgId}/${ROUTE_PATH.dieCuttingTools}`);
    }

    public openDocumentation(): void {
        window.open(this.#documentationUrl, '_blank');
    }

    public openErrorDialog(
        errorCode: string,
        requestType: ApiRequestType,
        deviceId: string,
        errorMessage: string
    ): void {
        this.#dialog.open<ClientApiErrorDialogComponent, ClientApiErrorDialogData, ClientApiErrorDialogResponse>(
            ClientApiErrorDialogComponent,
            {
                disableClose: true,
                data: { errorCode, requestType, deviceId, body: errorMessage },
            }
        );
    }

    public checkForErrorNotifications(notifications: HdmuiNotification[]): void {
        const palletSheetErrorTitle = this.#translateService.instant(
            'DC.ERROR_NOTIFICATION.ERR_PRINT_PALLETSHEET.ERROR_TITLE'
        );
        const palletSheetErrorNotification = notifications.find(
            (notification) =>
                notification.priority === HdmuiNotificationPriority.CRITICAL &&
                notification.title === palletSheetErrorTitle
        );
        if (palletSheetErrorNotification) {
            this.openErrorDialog(ErrorCode.ERR_PRINT_PALLETSHEET, ApiRequestType.PRINTPALLETSHEET, '', '{}');
            this.#notificationService.delete(palletSheetErrorNotification.id).pipe(take(1)).subscribe();
            return;
        }

        const errorNotification = notifications.find(
            (notification) =>
                notification.priority === HdmuiNotificationPriority.CRITICAL && notification.body.startsWith('ERR_')
        );
        if (errorNotification) {
            const errorBody = errorNotification.body.split(';');
            const errorMessage = this.parseHexToErrorNotification(errorBody[INDEX_3]);
            const requestType = errorBody[INDEX_1] as ApiRequestType;
            const machine = errorBody[INDEX_2];

            // OnPrem without status action confirmation: the client does not wait for the result, the user resolves the rejection
            // with Retry/Cancel (Cancel restores the last confirmed state)
            if (STATUS_ACTION_REQUEST_TYPES.includes(requestType) && this.isStatusActionConfirmationSupported()) {
                this.#snackBarService.openSnackBar(
                    this.#translateService.instant('DC.OPERATION_PAGE.SNACKBAR.ERROR'),
                    SnackbarClass.ERROR
                );
            } else {
                this.openErrorDialog(errorBody[INDEX_0], requestType, machine, JSON.stringify(errorMessage));
            }

            this.#notificationService.delete(errorNotification.id).pipe(take(1)).subscribe();
        }
    }

    public checkForNoMoreStacksNotification(notifications: HdmuiNotification[]): void {
        const noMoreStacksNotification = notifications.find((notification) => {
            const expectedMessage = this.#translateService.instant('BRING_TASK.NO_MORE_STACKS');
            return notification.title?.includes(expectedMessage) || notification.body?.includes(expectedMessage);
        });

        if (noMoreStacksNotification) {
            const message = noMoreStacksNotification.body || noMoreStacksNotification.title;

            this.#snackBarService.openSnackBar(message, SnackbarClass.WARNING);

            this.#notificationService.delete(noMoreStacksNotification.id).pipe(take(1)).subscribe();
        }
    }

    private isStatusActionConfirmationSupported(): boolean {
        return isFeatureEnabledForVersion(
            this.#organizationService.organizationInfo?.onPremVersion,
            FeatureRequiredOnPremVersion.STATUS_ACTION_CONFIRMATION
        );
    }

    private parseHexToErrorNotification(hex: string): ErrorNotification {
        const bytes = new Uint8Array(hex.length / 2);
        for (let i = 0; i < hex.length; i += 2) {
            bytes[i / 2] = Number.parseInt(hex.substring(i, i + 2), 16);
        }
        const json = new TextDecoder('utf-8').decode(bytes);

        try {
            return JSON.parse(json);
        } catch {
            return {} as ErrorNotification;
        }
    }
}

@NgModule({
    imports: [
        HdmuiMainApplicationHeaderComponent,
        CommonModule,
        TranslateModule,
        MatListModule,
        MatIconModule,
        MatTooltipModule,
        ChatbotWidgetIconButtonComponent,
    ],
    declarations: [ToolbarComponent],
    exports: [ToolbarComponent],
})
export class ToolbarComponentModule {}
